/**
 * Tour guiado que se lanza automáticamente la primera vez que el dueño
 * entra al dashboard. Se puede saltar en cualquier momento con "Saltar tour".
 *
 * Se marca como completado en localStorage: `bookvia_tour_completed`.
 * También se puede relanzar manualmente pasando `forceOpen`.
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Sparkles } from 'lucide-react';
import { tourSteps } from '@/lib/dashboardHelp';

const STORAGE_KEY = 'bookvia_tour_completed';

export default function GuidedTour({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-launch on first visit (only if not forced)
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStep(0);
      return;
    }
    try {
      const completed = window.localStorage.getItem(STORAGE_KEY) === '1';
      if (!completed) {
        // Small delay so the dashboard has time to render
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
  }, [forceOpen]);

  const finish = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
    if (onClose) onClose();
  };

  if (!open) return null;
  const current = tourSteps[step];
  const isLast = step === tourSteps.length - 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-2">
          <Badge className="bg-[#F05D5E] text-white hover:bg-[#F05D5E]">
            Paso {step + 1} de {tourSteps.length}
          </Badge>
          <div className="flex-1" />
          <button
            type="button"
            onClick={finish}
            className="text-xs text-neutral-500 hover:text-neutral-800"
            data-testid="tour-skip"
          >
            Saltar tour
          </button>
        </div>

        <h3 className="mb-2 text-xl font-semibold text-neutral-900">{current.title}</h3>
        <p className="mb-6 text-neutral-600">{current.body}</p>

        <div className="mb-5 flex gap-1">
          {tourSteps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-[#F05D5E]' : 'bg-neutral-200'
              }`}
            />
          ))}
        </div>

        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="tour-prev"
          >
            Anterior
          </Button>
          <Button
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            className="bg-[#F05D5E] hover:bg-[#d64431]"
            data-testid="tour-next"
          >
            {isLast ? (
              <>
                <Sparkles className="mr-1 h-4 w-4" />
                ¡Empezar!
              </>
            ) : (
              <>
                Siguiente
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
